/**
 * Voice Cloning Hooks
 *
 * React hooks for voice cloning feature.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { voiceApi } from '../services/voiceApi';
import { useAuth } from './useAuth';
import type { IVoiceInfo, IVoiceUploadRequest, UploadProgressCallback } from '../types/voice.types';
import { ensureArray } from '../utils/apiNormalizers';
import { handleError } from '../utils/errorHandler';

/**
 * Hook for managing user voices.
 *
 * This hook waits for authentication to be ready before fetching voices.
 * It also re-fetches when the user logs in or changes.
 */
export function useVoices() {
  const [voices, setVoices] = useState<IVoiceInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, loading: authLoading } = useAuth();
  const hasFetchedRef = useRef(false);

  /**
   * Fetch all voices for current user.
   */
  const fetchVoices = useCallback(
    async (includeUrls = false) => {
      // Don't fetch if not authenticated
      if (!user) {
        setVoices([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await voiceApi.listVoices(includeUrls);
        // Defensive check: ensure voices is always an array (using centralized normalizer)
        setVoices(ensureArray(response.voices));
      } catch (err) {
        setError(handleError(err, 'Voices', 'Failed to fetch voices'));
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  /**
   * Delete a voice.
   */
  const deleteVoice = useCallback(async (voiceId: string) => {
    try {
      await voiceApi.deleteVoice(voiceId);
      setVoices((prev) => prev.filter((v) => v.voice_id !== voiceId));
      return true;
    } catch (err) {
      setError(handleError(err, 'Voices', 'Failed to delete voice'));
      return false;
    }
  }, []);

  /**
   * Fetch voices when user is authenticated.
   * Only fetches once per user session to avoid unnecessary API calls.
   */
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // If user is authenticated and we haven't fetched yet
    if (user && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchVoices(true);
    }

    // If user logs out, clear voices and reset fetch flag
    if (!user) {
      setVoices([]);
      hasFetchedRef.current = false;
    }
  }, [user, authLoading, fetchVoices]);

  return {
    voices,
    loading: loading || authLoading,
    error,
    fetchVoices,
    deleteVoice,
  };
}

/**
 * Hook for voice upload.
 */
export function useVoiceUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  /**
   * Upload a voice file.
   */
  const uploadVoice = useCallback(
    async (file: File, metadata: IVoiceUploadRequest, onProgress?: UploadProgressCallback) => {
      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        // Validate file first
        const validation = await voiceApi.validateAudioFile(file);
        if (!validation.isValid) {
          throw new Error(validation.errorMessage);
        }

        // Upload with progress tracking
        const result = await voiceApi.uploadVoice(file, metadata, (p) => {
          setProgress(p);
          onProgress?.(p);
        });

        setProgress(100);
        return result;
      } catch (err) {
        setError(handleError(err, 'VoiceUpload', 'Upload failed'));
        throw err;
      } finally {
        setUploading(false);
      }
    },
    []
  );

  /**
   * Reset upload state.
   */
  const resetUpload = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setError(null);
  }, []);

  return {
    uploading,
    progress,
    error,
    uploadVoice,
    resetUpload,
  };
}

/**
 * Hook for audio validation.
 */
export function useAudioValidation() {
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<{
    isValid: boolean;
    errorMessage?: string;
    warningMessage?: string;
    durationSeconds?: number;
  } | null>(null);

  /**
   * Validate an audio file.
   */
  const validateFile = useCallback(async (file: File) => {
    setValidating(true);
    setValidation(null);

    try {
      const result = await voiceApi.validateAudioFile(file);
      setValidation(result);
      return result;
    } finally {
      setValidating(false);
    }
  }, []);

  /**
   * Reset validation state.
   */
  const resetValidation = useCallback(() => {
    setValidation(null);
  }, []);

  return {
    validating,
    validation,
    validateFile,
    resetValidation,
  };
}
